/**
 * Position Thesis API client methods.
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';
import {
  StructuredThesis,
  ThesisCreateRequest,
  ThesisUpdateRequest,
  parseThesisText,
  EMPTY_STRUCTURED_THESIS,
} from '../../types/thesis';

/**
 * Position thesis with enriched parsed structured data.
 * The thesis_text field contains JSON string from API,
 * enriched with parsed statement, pillars, and risks.
 */
export interface PositionThesis {
  id: number;
  position_id: number | null;
  watchlist_item_id?: number | null;
  user_id: number;
  thesis_text: string | null;
  stop_loss_price: number | null;
  take_profit_price: number | null;
  entry_price?: number | null;
  remind_at?: string | null;
  conviction_level: number | null;
  duration: 'Core' | 'Growth' | 'Speculative' | null;
  /** Single primary theme (Q5 §6.2); backend infers one when omitted */
  theme?: string | null;
  updated_at: string;
  /** Enriched parsed structured thesis data */
  structuredThesis?: StructuredThesis;
}

/**
 * @deprecated Use ThesisCreateRequest from types/thesis.ts instead
 */
export interface PositionThesisCreateRequest {
  position_id: number;
  thesis_text: string;
  stop_loss_price?: number | null;
  take_profit_price?: number | null;
  conviction_level?: number | null;
  duration?: 'Core' | 'Growth' | 'Speculative' | null;
}

/**
 * @deprecated Use ThesisUpdateRequest from types/thesis.ts instead
 */
export interface PositionThesisUpdateRequest {
  thesis_text?: string;
  stop_loss_price?: number | null;
  take_profit_price?: number | null;
  conviction_level?: number | null;
  duration?: 'Core' | 'Growth' | 'Speculative' | null;
}

/**
 * Enrich a raw API thesis response with parsed structured thesis data.
 *
 * @param thesis - Raw thesis data from API
 * @returns Thesis with enriched structuredThesis field
 */
function enrichThesisWithParsed<T extends { thesis_text: string | null }>(
  thesis: T
): T & { structuredThesis: StructuredThesis } {
  const structuredThesis = parseThesisText(thesis.thesis_text);
  return { ...thesis, structuredThesis };
}

/**
 * Get all thesis versions for a specific position.
 * Returns array of theses sorted by updated_at DESC (latest first).
 * Empty array if no thesis exists.
 *
 * Multi-version support: Each position can have multiple thesis versions.
 */
export async function getPositionTheses(positionId: number): Promise<PositionThesis[]> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const url = new URL(`${API_CONFIG.baseURL}/api/v1/position-theses`);
  url.searchParams.append('position_id', positionId.toString());

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get position thesis');
  }

  const data = await response.json();
  const theses = data || [];
  // Enrich each thesis with parsed structured data
  return theses.map((t: PositionThesis) => enrichThesisWithParsed(t));
}

/**
 * Get all thesis versions for a watchlist item.
 * Returns array of theses sorted by updated_at DESC (latest first).
 * Empty array if no thesis exists.
 */
export async function getWatchlistThesis(watchlistItemId: number): Promise<PositionThesis[]> {
  const token = await getToken();
  if (!token) throw new Error('Authentication required');
  const url = new URL(`${API_CONFIG.baseURL}/api/v1/position-theses`);
  url.searchParams.append('watchlist_item_id', watchlistItemId.toString());
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to get thesis');
  const data = await response.json();
  return (data || []).map((t: PositionThesis) => enrichThesisWithParsed(t));
}

export interface WatchlistThesisCreateRequest {
  watchlist_item_id: number;
  structured_thesis?: StructuredThesis | null;
  thesis_text?: string | null;
  conviction_level?: number | null;
  duration?: 'Core' | 'Growth' | 'Speculative' | null;
  entry_price?: number | null;
  remind_at?: string | null;
}

export async function createWatchlistThesis(
  opts: WatchlistThesisCreateRequest
): Promise<PositionThesis> {
  const token = await getToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-theses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(opts),
  });
  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      throw new Error(error.detail.map((e: any) => e.msg || e.message).join(', '));
    }
    throw new Error(error.detail || 'Failed to create thesis');
  }
  return enrichThesisWithParsed(await response.json());
}

/**
 * Add a new thesis to a position.
 *
 * Supports both legacy thesis_text and new structured_thesis formats.
 * At least one of thesisText or structuredThesis must be provided.
 *
 * @param positionId - Position snapshot ID
 * @param thesisText - Legacy plain text thesis (optional if structuredThesis provided)
 * @param stopLossPrice - Stop-loss price target
 * @param takeProfitPrice - Take-profit price target
 * @param convictionLevel - Conviction level 1-5
 * @param duration - Investment strategy duration
 * @param structuredThesis - Structured thesis data with statement, pillars, risks
 * @param entryPrice - Entry price for watchlist thesis (optional)
 * @param remindAt - Reminder date for watchlist thesis (optional)
 */
export async function createPositionThesis(
  positionId: number,
  thesisText: string,
  stopLossPrice?: number | null,
  takeProfitPrice?: number | null,
  convictionLevel?: number | null,
  duration?: 'Core' | 'Growth' | 'Speculative' | null,
  structuredThesis?: StructuredThesis | null,
  entryPrice?: number | null,
  remindAt?: string | null
): Promise<PositionThesis> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const request: ThesisCreateRequest = {
    position_id: positionId,
    thesis_text: thesisText,
    stop_loss_price: stopLossPrice,
    take_profit_price: takeProfitPrice,
    conviction_level: convictionLevel,
    duration: duration,
    structured_thesis: structuredThesis,
    entry_price: entryPrice,
    remind_at: remindAt,
  };

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-theses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    // Handle FastAPI validation errors (array of error objects)
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to create thesis');
  }

  const data = await response.json();
  return enrichThesisWithParsed(data);
}

/**
 * Update an existing position thesis.
 *
 * Accepts both legacy PositionThesisUpdateRequest and new ThesisUpdateRequest formats.
 * The new format supports structured_thesis field.
 *
 * @param thesisId - Thesis ID to update
 * @param thesisData - Update data (supports both legacy and structured formats)
 */
export async function updatePositionThesis(
  thesisId: number,
  thesisData: PositionThesisUpdateRequest | ThesisUpdateRequest
): Promise<PositionThesis> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-theses/${thesisId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(thesisData),
  });

  if (!response.ok) {
    const error = await response.json();
    // Handle FastAPI validation errors (array of error objects)
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to update thesis');
  }

  const data = await response.json();
  return enrichThesisWithParsed(data);
}

/**
 * Delete a position thesis.
 */
export async function deletePositionThesis(thesisId: number): Promise<void> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-theses/${thesisId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    // Handle FastAPI validation errors (array of error objects)
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to delete thesis');
  }
}
